import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAllCycles, type EPRSetupCycle } from '../services/eprSetupService';
import { fetchDirectory, suggestReviewerPanel, type SuggestedReviewer } from '../services/directoryService';
import { createInvitationBatch, type CreateInvitationInput } from '../services/reviewInvitationService';
import { addDoc, collection, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { REVIEWER_ROLE_LABELS, type ReviewerRole, type DirectoryEmployee } from '../types/magicLink';
import { EMPLOYEE_LEVEL_LABELS, type EmployeeLevel } from '../types/optics2026';
import { useToast } from '../components/ui/Toast';
import { getEprBaseUrl } from '../routes';

type Step = 1 | 2 | 3;

interface PanelRow extends SuggestedReviewer {
  selected: boolean;
}

const MagicLinkDispatcher: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [step, setStep] = useState<Step>(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copiedTokens, setCopiedTokens] = useState<Set<string>>(new Set());

  const [cycles, setCycles] = useState<EPRSetupCycle[]>([]);
  const [directory, setDirectory] = useState<DirectoryEmployee[]>([]);
  const [loaded, setLoaded] = useState(false);

  const [revieweeId, setRevieweeId] = useState('');
  const [cycleId, setCycleId]       = useState('');
  const [expiryDays, setExpiryDays] = useState(14);
  const [search, setSearch]         = useState('');

  const [panel, setPanel] = useState<PanelRow[]>([]);
  const [adhoc, setAdhoc] = useState<{ name: string; email: string; role: ReviewerRole }>({
    name: '', email: '', role: 'peer',
  });

  const [results, setResults] = useState<Array<{ token: string; url: string; reviewerEmail: string; reviewerRole: ReviewerRole }>>([]);
  const [createdFormId, setCreatedFormId] = useState<string>('');

  // ─── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        console.log('[Dispatcher] fetching cycles…');
        const csP = fetchAllCycles().then(r => { console.log('[Dispatcher] cycles:', r.length); return r; });
        console.log('[Dispatcher] fetching directory…');
        const dirP = fetchDirectory().then(r => { console.log('[Dispatcher] directory:', r.length); return r; });
        const [cs, dir] = await Promise.all([csP, dirP]);
        if (!alive) return;
        const activeCycles = cs.filter(c => c.status === 'active' || c.status === 'draft');
        setCycles(activeCycles);
        if (activeCycles.length === 1) setCycleId(activeCycles[0].id);
        setDirectory(dir);
        setLoaded(true);
        console.log('[Dispatcher] ready', { cycles: activeCycles.length, directory: dir.length });
      } catch (e) {
        console.error('[Dispatcher] init failed:', e);
        setError(String((e as Error).message ?? e));
        setLoaded(true);  // show error UI instead of permanent loading
      }
    })();
    return () => { alive = false; };
  }, []);

  const reviewee = useMemo(
    () => directory.find(e => e.id === revieweeId),
    [directory, revieweeId],
  );

  // Audit #21 — also return the unfiltered total so the truncation hint can render
  const filteredDirectory = useMemo(() => {
    const s = search.toLowerCase().trim();
    const matches = s
      ? directory.filter(e => e.name.toLowerCase().includes(s) || e.email.toLowerCase().includes(s))
      : directory;
    return { rows: matches.slice(0, 50), totalMatching: matches.length };
  }, [directory, search]);

  // ─── Step 1 → Step 2: build suggested panel ────────────────────────────────
  const goToStep2 = () => {
    if (!reviewee || !cycleId) return;
    const suggested = suggestReviewerPanel(reviewee, directory);
    setPanel(suggested.map(s => ({ ...s, selected: true })));
    setStep(2);
  };

  // ─── Add ad-hoc reviewer (not in directory) ────────────────────────────────
  const addAdhoc = () => {
    const email = adhoc.email.trim().toLowerCase();
    const name  = adhoc.name.trim();
    if (!email || !name) return;
    if (panel.some(p => p.employee.email === email)) return;
    const adhocEmp: DirectoryEmployee = {
      id: `adhoc-${email}`,
      name,
      email,
      isActive: true,
    };
    setPanel(p => [...p, { reviewerRole: adhoc.role, employee: adhocEmp, selected: true }]);
    setAdhoc({ name: '', email: '', role: 'peer' });
  };

  // ─── Step 3: dispatch ──────────────────────────────────────────────────────
  const dispatch = async () => {
    if (!reviewee || !cycleId || !user) return;
    setBusy(true); setError('');
    try {
      // 1. Create the parent reviewForm doc
      const formRef = await addDoc(collection(db, 'reviewForms'), {
        cycleId,
        revieweeId: reviewee.id,
        revieweeName: reviewee.name,
        reviewerId: '', // legacy field — magic-link form does not have a single reviewer
        status: 'self_pending',
        selfReview: {},
        managerReview: {},
        responses360Ids: [],
        auditLog: [{
          action: 'created_via_magic_link_dispatcher',
          performedBy: user.uid,
          // serverTimestamp() not allowed inside array elements — use client timestamp.
          timestamp: Timestamp.now(),
        }],
        createdAt: serverTimestamp(),
        // mark as new framework so consumer queries can branch:
        framework: 'optics_2026',
      });

      // 2. Build invitation inputs.
      // baseUrl from routes.getEprBaseUrl() — pins tokens to the
      // canonical domain (epr-staging.rascalrepublic.com for staging,
      // epr.rascalrepublic.com for prod, set via VITE_EPR_URL per env)
      // regardless of which hostname HR opened this admin tab on
      // (e.g. *.web.app fallback). ENGINEERING.md §3e.
      const baseUrl = getEprBaseUrl();
      const selected = panel.filter(p => p.selected);
      const inputs: CreateInvitationInput[] = selected.map(p => ({
        reviewFormId: formRef.id,
        cycleId,
        revieweeId: reviewee.id,
        revieweeName: reviewee.name,
        reviewerRole: p.reviewerRole,
        reviewerEmail: p.employee.email,
        reviewerId: p.employee.id.startsWith('adhoc-') ? undefined : p.employee.id,
        reviewerName: p.employee.name,
        expiryDays,
      }));

      const sent = await createInvitationBatch(inputs, baseUrl);
      setResults(sent);
      setCreatedFormId(formRef.id);
      setStep(3);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(1);
    setRevieweeId('');
    setPanel([]);
    setResults([]);
    setError('');
    setCopiedTokens(new Set());
  };

  // Audit #20 — copy-link with Toast feedback so HR knows it worked
  const copyLink = async (url: string, reviewerEmail: string, token: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedTokens(prev => new Set(prev).add(token));
      toast.show(`Link for ${reviewerEmail.split('@')[0]} copied`, { icon: 'content_copy' });
      // Revert button state after 2.5s
      window.setTimeout(() => {
        setCopiedTokens(prev => {
          const next = new Set(prev);
          next.delete(token);
          return next;
        });
      }, 2500);
    } catch {
      toast.show('Copy failed — please select the link manually', { icon: 'error', duration: 3500 });
    }
  };

  // Audit #22 — allow jumping back to earlier steps via chip click
  const canRevisit = (target: Step) => target < step && step !== 3;
  const handleStepClick = (target: Step) => {
    if (!canRevisit(target)) return;
    setStep(target);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  if (!loaded) return <div className="p-8 text-stone-500">Loading directory…</div>;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] text-stone-500 uppercase tracking-wider mb-1">HR Workflow</div>
          <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">Start a Review</h1>
          <p className="text-sm text-stone-600 max-w-2xl mt-1">
            Pick who's being reviewed, choose the reviewers, and we'll send each one a unique
            link. Reviewers just click and fill — no login needed.
          </p>
        </div>
        <Link to="/reviews" className="text-sm text-blue-600 hover:underline mt-1">← All reviews</Link>
      </div>

      {/* Step indicator — chips clickable to revisit completed steps (audit #22) */}
      <div className="flex gap-2 mb-6">
        {([
          { n: 1, label: 'Set up' },
          { n: 2, label: 'Choose reviewers' },
          { n: 3, label: 'Sent' },
        ] as const).map(s => {
          const isActive = step === s.n;
          const isCompleted = step > s.n;
          const isRevisitable = canRevisit(s.n as Step);
          return (
            <button
              key={s.n}
              type="button"
              onClick={() => handleStepClick(s.n as Step)}
              disabled={!isRevisitable && !isActive}
              aria-current={isActive ? 'step' : undefined}
              className={[
                'flex-1 px-4 py-2.5 rounded-lg border text-xs font-medium flex items-center gap-2 text-left',
                'transition-colors',
                isActive
                  ? 'bg-stone-900 text-white border-stone-900 cursor-default'
                  : isCompleted
                    ? `bg-emerald-50 text-emerald-800 border-emerald-200 ${
                        isRevisitable ? 'hover:bg-emerald-100 cursor-pointer' : 'cursor-default'
                      }`
                    : 'bg-white text-stone-500 border-stone-200 cursor-not-allowed',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-500',
              ].join(' ')}
            >
              <span className={[
                'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0',
                isActive ? 'bg-white text-stone-900' :
                isCompleted ? 'bg-emerald-200 text-emerald-900' :
                              'bg-stone-100 text-stone-500',
              ].join(' ')}>
                {isCompleted ? (
                  <span className="material-symbols-rounded" style={{ fontSize: 12 }} aria-hidden="true">check</span>
                ) : s.n}
              </span>
              <span className="truncate">{s.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── Step 1: Set up ─────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">Review cycle</label>
              <select
                value={cycleId}
                onChange={e => setCycleId(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm bg-white"
              >
                <option value="">— Select cycle —</option>
                {cycles.map(c => (
                  <option key={c.id} value={c.id}>{c.name} {c.status === 'draft' ? '(draft)' : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">Link expiry</label>
              <select
                value={expiryDays}
                onChange={e => setExpiryDays(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm bg-white"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={21}>21 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>
          </div>

          <div className="mt-5">
            <label className="block text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">Subject employee (reviewee)</label>
            <input
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm mb-2"
            />
            <div className="border border-stone-200 rounded-md max-h-72 overflow-y-auto">
              {filteredDirectory.rows.length === 0 && (
                <div className="p-3 text-xs text-stone-500">No matches.</div>
              )}
              {filteredDirectory.rows.map(e => (
                <button
                  key={e.id}
                  onClick={() => setRevieweeId(e.id)}
                  className={[
                    'w-full text-left px-3 py-2 text-sm border-b border-stone-100 last:border-0 flex justify-between items-center',
                    revieweeId === e.id ? 'bg-stone-100' : 'hover:bg-stone-50',
                  ].join(' ')}
                >
                  <div>
                    <div className="font-medium text-stone-900">{e.name}</div>
                    <div className="text-[11px] text-stone-500">
                      {e.email} {e.unitName && `· ${e.unitName}`} {e.jobTitle && `· ${e.jobTitle}`}
                    </div>
                  </div>
                  {e.level && (
                    <span className="text-[10px] text-stone-600 bg-stone-100 px-2 py-0.5 rounded">
                      {EMPLOYEE_LEVEL_LABELS[e.level as EmployeeLevel]}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {/* Audit #21 — surface silent truncation so HR knows to refine search */}
            {filteredDirectory.totalMatching > filteredDirectory.rows.length && (
              <p className="text-xs text-stone-500 mt-2 flex items-center gap-1.5">
                <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">info</span>
                Showing {filteredDirectory.rows.length} of {filteredDirectory.totalMatching} matches — refine your search to narrow down.
              </p>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-stone-200 flex justify-end">
            <button
              onClick={goToStep2}
              disabled={!revieweeId || !cycleId}
              className="px-5 py-2 bg-stone-900 text-white text-sm font-medium rounded-md disabled:opacity-40 hover:bg-stone-800"
            >
              Next: Choose reviewers →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Choose reviewers ─────────────────────────────────────── */}
      {step === 2 && reviewee && (
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
            <div className="mb-4">
              <div className="text-[11px] text-stone-500 uppercase tracking-wider mb-1">Reviewing</div>
              <div className="text-base font-semibold text-stone-900">{reviewee.name}</div>
              <div className="text-xs text-stone-500">
                {reviewee.email} {reviewee.unitName && `· ${reviewee.unitName}`}
                {reviewee.level && ` · ${EMPLOYEE_LEVEL_LABELS[reviewee.level as EmployeeLevel]}`}
              </div>
            </div>

            <div className="text-[11px] text-stone-500 uppercase tracking-wider mb-2 flex justify-between">
              <span>Reviewers · suggested from your team directory</span>
              <span>{panel.filter(p => p.selected).length} selected</span>
            </div>

            <div className="space-y-1.5 mb-4">
              {panel.map((p, idx) => (
                <label
                  key={`${p.employee.id}-${p.reviewerRole}`}
                  className={[
                    'flex items-center gap-3 p-2.5 border rounded-md cursor-pointer',
                    p.selected ? 'border-stone-300 bg-white' : 'border-stone-200 bg-stone-50 opacity-60',
                  ].join(' ')}
                >
                  <input
                    type="checkbox"
                    checked={p.selected}
                    onChange={() => setPanel(arr => arr.map((row, i) => i === idx ? { ...row, selected: !row.selected } : row))}
                    className="accent-stone-900"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-900 truncate">{p.employee.name}</div>
                    <div className="text-[11px] text-stone-500 truncate">{p.employee.email}</div>
                  </div>
                  <span className="text-[10px] text-stone-700 bg-stone-100 px-2 py-0.5 rounded uppercase tracking-wider shrink-0">
                    {REVIEWER_ROLE_LABELS[p.reviewerRole]}
                  </span>
                  <button
                    onClick={() => setPanel(arr => arr.filter((_, i) => i !== idx))}
                    className="text-stone-400 hover:text-red-600 text-sm px-1"
                    title="Remove"
                  >
                    ×
                  </button>
                </label>
              ))}
            </div>

            {/* Add ad-hoc */}
            <details className="mt-4 border-t border-stone-100 pt-4">
              <summary className="text-xs text-stone-600 cursor-pointer hover:text-stone-900">+ Add reviewer not in directory</summary>
              <div className="mt-3 grid grid-cols-12 gap-2">
                <input
                  type="text" placeholder="Name"
                  value={adhoc.name}
                  onChange={e => setAdhoc(a => ({ ...a, name: e.target.value }))}
                  className="col-span-3 px-2 py-1.5 border border-stone-300 rounded text-sm"
                />
                <input
                  type="email" placeholder="email@..."
                  value={adhoc.email}
                  onChange={e => setAdhoc(a => ({ ...a, email: e.target.value }))}
                  className="col-span-5 px-2 py-1.5 border border-stone-300 rounded text-sm"
                />
                <select
                  value={adhoc.role}
                  onChange={e => setAdhoc(a => ({ ...a, role: e.target.value as ReviewerRole }))}
                  className="col-span-2 px-2 py-1.5 border border-stone-300 rounded text-sm"
                >
                  <option value="peer">Peer</option>
                  <option value="line_manager">Line Mgr</option>
                  <option value="direct_report">Direct Rep</option>
                </select>
                <button
                  onClick={addAdhoc}
                  className="col-span-2 bg-stone-900 text-white text-xs font-medium rounded hover:bg-stone-800"
                >
                  Add
                </button>
              </div>
            </details>

            <div className="mt-6 pt-4 border-t border-stone-200 flex justify-between items-center">
              <button onClick={() => setStep(1)} className="text-sm text-stone-600 hover:text-stone-900">← Back</button>
              <button
                onClick={dispatch}
                disabled={busy || panel.filter(p => p.selected).length === 0}
                className="px-5 py-2 bg-stone-900 text-white text-sm font-medium rounded-md disabled:opacity-40 hover:bg-stone-800"
              >
                {busy ? 'Sending…' : `Send to ${panel.filter(p => p.selected).length} reviewer${panel.filter(p => p.selected).length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900 leading-relaxed">
            <div className="font-medium mb-2">Who sees what</div>
            <div className="space-y-1.5">
              <div>
                <b>Shown with names:</b> the employee's own self-review and their line manager's review.
              </div>
              <div>
                <b>Kept anonymous:</b> peer reviews and direct-report reviews. Reports show only
                the average score — never individual answers or who wrote them.
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-amber-200">
              <div className="font-medium mb-1">What happens next</div>
              For now, copy each reviewer's link from the next step and send it manually
              (email or WhatsApp). Automatic email + WhatsApp reminders are coming soon.
            </div>
          </div>
        </div>
      )}

      {/* ── Step 3: Sent ─────────────────────────────────────────────────── */}
      {step === 3 && (
        <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 text-lg">✓</div>
            <div>
              <div className="text-base font-semibold text-stone-900">{results.length} reviewer link{results.length === 1 ? '' : 's'} ready to share</div>
              <div className="text-xs text-stone-500">Copy each link and send it to the reviewer (email, WhatsApp, or any chat). Each link is unique and one-time use.</div>
            </div>
          </div>

          <div className="space-y-2">
            {results.map(r => {
              const isCopied = copiedTokens.has(r.token);
              return (
                <div key={r.token} className="border border-stone-200 rounded-md p-3 flex justify-between items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-stone-500 uppercase tracking-wider">{REVIEWER_ROLE_LABELS[r.reviewerRole]}</div>
                    <div className="text-sm font-medium text-stone-900">{r.reviewerEmail}</div>
                    <div className="text-[11px] text-stone-500 truncate font-mono">{r.url}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void copyLink(r.url, r.reviewerEmail, r.token)}
                    className={[
                      'h-9 px-3 inline-flex items-center gap-1.5 text-xs font-medium rounded shrink-0 transition-colors',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-500',
                      isCopied
                        ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'
                        : 'bg-stone-100 text-stone-700 hover:bg-stone-200',
                    ].join(' ')}
                    aria-label={isCopied ? `Copied link for ${r.reviewerEmail}` : `Copy link for ${r.reviewerEmail}`}
                  >
                    <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
                      {isCopied ? 'check' : 'content_copy'}
                    </span>
                    {isCopied ? 'Copied' : 'Copy link'}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-4 border-t border-stone-200 flex justify-between items-center">
            <button onClick={reset} className="text-sm text-stone-600 hover:text-stone-900">+ Start another review</button>
            {createdFormId && (
              <Link
                to={`/reviews/${createdFormId}`}
                className="px-4 py-2 bg-emerald-700 text-white text-sm font-medium rounded-md hover:bg-emerald-800"
              >
                Monitor this review →
              </Link>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-800">{error}</div>
      )}
    </div>
  );
};

export default MagicLinkDispatcher;
